import { NextImageVariant } from "./NextImageVariant";
import { PictureVariant } from "./PictureVariant";
import type { ResponsiveImageProps } from "./types";
import { getResponsiveImageUrls } from "./utils";

/**
 * ResponsiveImage component that handles responsive images using HTML5 picture element
 * or Next.js Image component based on props.
 *
 * @example
 * // Background image with fill
 * <ResponsiveImage
 *   src={module.backgroundImage}
 *   fill
 *   className="absolute inset-0 object-cover"
 *   priority
 * />
 *
 * @example
 * // Picture element for different images per breakpoint
 * <ResponsiveImage
 *   src={module.media}
 *   className="w-full h-auto"
 *   objectFit="contain"
 * />
 *
 * @example
 * // Fixed dimensions with Next.js Image
 * <ResponsiveImage
 *   src={card.image}
 *   width={400}
 *   height={300}
 *   className="rounded-lg"
 *   useNextImage
 * />
 */
export function ResponsiveImage({
  src,
  fill,
  width,
  height,
  useNextImage,
  priority = false,
  quality,
  sizes,
  className,
  objectFit = "cover",
  objectPosition,
  imageOptions,
  breakpoints,
  alt,
  ...props
}: ResponsiveImageProps) {
  // Get responsive image URLs
  const imageData = getResponsiveImageUrls(src, imageOptions);

  if (!imageData) return null;

  // Override alt text if provided
  const finalAlt = alt ?? imageData.alt;

  // Check if we have multiple responsive images (different images per breakpoint)
  const hasMultipleBreakpoints =
    (imageData.mobile && imageData.tablet) ||
    (imageData.mobile && imageData.desktop) ||
    (imageData.tablet && imageData.desktop);

  // Determine which variant to use
  // Prioritize Next.js Image for optimization when possible:
  // - Explicitly requested: always use NextImageVariant
  // - Fill mode: default to NextImageVariant (unless multiple breakpoints AND useNextImage is explicitly false)
  // - Fixed dimensions: use NextImageVariant when width/height provided
  // - Multiple breakpoints: use PictureVariant for art direction (unless useNextImage is explicitly true)
  const shouldUseNextImage =
    // Explicitly requested
    useNextImage === true ||
    // Fill mode: use NextImageVariant by default, unless we have multiple breakpoints AND useNextImage is false
    (fill === true && (!hasMultipleBreakpoints || useNextImage !== false)) ||
    // Fixed dimensions: use NextImageVariant
    (width !== undefined && height !== undefined && !hasMultipleBreakpoints);

  if (shouldUseNextImage) {
    // Use Next.js Image (single image, optimized)
    return (
      <NextImageVariant
        imageData={{ ...imageData, alt: finalAlt }}
        fill={fill}
        width={width}
        height={height}
        priority={priority}
        quality={quality}
        sizes={sizes}
        className={className}
        objectFit={objectFit}
        objectPosition={objectPosition}
        {...props}
      />
    );
  }

  // Default: Use picture element with regular img (handles responsive images)
  // This works for fill mode too - picture element can be absolutely positioned
  return (
    <PictureVariant
      imageData={{ ...imageData, alt: finalAlt }}
      fill={fill}
      className={className}
      objectFit={objectFit}
      objectPosition={objectPosition}
      breakpoints={breakpoints}
      {...props}
    />
  );
}
